import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { getInfluenceIndex, getInfluenceEntry } from '../services/api';

const SERIF_FONT = "'Georgia', 'Times New Roman', serif";

// ============================================
// INFLUENCE INDEX PAGE - La tecnologia Style
// ============================================
export const InfluenceIndexPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getInfluenceIndex();
        setEntries(data || []);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const types = ['all', 'persona', 'empresa', 'organización', 'gobierno'];
  const filteredEntries = filter === 'all'
    ? entries
    : entries.filter(e => e.entity_type?.toLowerCase() === filter);

  // Sort by score
  const sortedEntries = [...filteredEntries].sort((a, b) => (b.score || 0) - (a.score || 0));

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Page Header */}
      <div className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <h1 className="text-4xl font-bold" style={{ fontFamily: SERIF_FONT }}>
            Índice de Influencia
          </h1>
          <p className="text-gray-400 mt-2">Ranking semanal de las personas y entidades más influyentes</p>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-gray-200 bg-white sticky top-16 z-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-6 py-4 overflow-x-auto">
            {types.map((t) => (
              <button
                key={t}
                onClick={() => setFilter(t)}
                className={`text-sm font-medium whitespace-nowrap pb-2 border-b-2 transition-colors ${
                  filter === t
                    ? 'text-gray-900 border-blue-600'
                    : 'text-gray-500 border-transparent hover:text-gray-900'
                }`}
              >
                {t === 'all' ? 'Todos' : t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Top 3 */}
      {sortedEntries.length >= 3 && (
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 py-12">
            <div className="grid md:grid-cols-3 gap-8">
              {sortedEntries.slice(0, 3).map((entry, i) => (
                <div
                  key={entry.entry_id}
                  className={`relative p-6 rounded-xl ${
                    i === 0 ? 'bg-yellow-50 border-2 border-yellow-400' :
                    i === 1 ? 'bg-gray-100 border-2 border-gray-300' :
                    'bg-orange-50 border-2 border-orange-300'
                  }`}
                >
                  <div className="absolute -top-4 left-6">
                    <span className={`text-4xl font-bold ${
                      i === 0 ? 'text-yellow-500' :
                      i === 1 ? 'text-gray-400' :
                      'text-orange-400'
                    }`}>
                      #{i + 1}
                    </span>
                  </div>
                  <div className="mt-4">
                    <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: SERIF_FONT }}>
                      {entry.entity_name}
                    </h3>
                    <p className="text-sm text-gray-500 capitalize mt-1">{entry.entity_type}</p>
                    <div className="flex items-center gap-4 mt-4">
                      <span className="text-3xl font-bold text-gray-900">{entry.score}</span>
                      <span className={`flex items-center gap-1 text-sm font-medium ${
                        entry.movement > 0 ? 'text-green-600' :
                        entry.movement < 0 ? 'text-[#1e3a5f]' :
                        'text-gray-500'
                      }`}>
                        {entry.movement > 0 ? <TrendingUp className="w-4 h-4" /> :
                         entry.movement < 0 ? <TrendingDown className="w-4 h-4" /> :
                         <Minus className="w-4 h-4" />}
                        {entry.movement > 0 ? '+' : ''}{entry.movement}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-4 line-clamp-2">{entry.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Full Ranking */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: SERIF_FONT }}>
          Ranking Completo
        </h2>
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">#</th>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Entidad</th>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Tipo</th>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Puntuación</th>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Cambio</th>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Razón</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedEntries.map((entry, i) => (
                <tr key={entry.entry_id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <span className="text-lg font-bold text-gray-400">{i + 1}</span>
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-bold text-gray-900">{entry.entity_name}</p>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="outline" className="capitalize">{entry.entity_type}</Badge>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-xl font-bold text-gray-900">{entry.score}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`flex items-center gap-1 text-sm font-medium ${
                      entry.movement > 0 ? 'text-green-600' :
                      entry.movement < 0 ? 'text-[#1e3a5f]' :
                      'text-gray-500'
                    }`}>
                      {entry.movement > 0 ? <TrendingUp className="w-4 h-4" /> :
                       entry.movement < 0 ? <TrendingDown className="w-4 h-4" /> :
                       <Minus className="w-4 h-4" />}
                      {entry.movement > 0 ? '+' : ''}{entry.movement}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-600 line-clamp-1">{entry.reason}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {sortedEntries.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No hay entradas en el índice
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// INFLUENCE ENTRY PAGE (placeholder)
// ============================================
export const InfluenceEntryPage = () => {
  const { id } = useParams();
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-gray-500">Detalle de entrada: {id}</div>
    </div>
  );
};

export default InfluenceIndexPage;
